import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { User, Mail, Phone, MessageCircle } from 'lucide-react';
import { useConversation } from '@/contexts/ConversationContext';
import { guestRsvpLabel } from '@/components/guests/guestRows';

interface UserInfo {
  id: number;
  full_name: string;
  email?: string;
  phone?: string;
}

interface UserProfilePopoverProps {
  user?: UserInfo;
  children: React.ReactNode;
  celebrationId?: number;
  /** Responses already authorized and loaded by this screen; never fetched globally. */
  rsvpContext?: ReadonlyArray<{ eventId: number; eventTitle: string; status?: string }>;
}

export const UserProfilePopover: React.FC<UserProfilePopoverProps> = ({ user, children, celebrationId, rsvpContext }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isClickMode, setIsClickMode] = useState(false);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const popoverId = React.useId();
  const { openQuickMessage } = useConversation();
  const name = user?.full_name || 'Unknown guest';

  const cancelClose = () => clearTimeout(closeTimer.current);
  const open = () => {
    cancelClose();
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setPosition({
      top: Math.max(8, Math.min(rect.bottom + 8, window.innerHeight - 320)),
      left: Math.max(8, Math.min(rect.left, window.innerWidth - 320)),
    });
    setIsOpen(true);
  };

  const closeAfterLeave = () => {
    cancelClose();
    if (isClickMode) return;
    closeTimer.current = setTimeout(() => {
      if (!triggerRef.current?.contains(document.activeElement) && !popoverRef.current?.contains(document.activeElement)) setIsOpen(false);
    }, 150);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOpen && isClickMode) {
      setIsOpen(false);
      setIsClickMode(false);
    } else {
      open();
      setIsClickMode(true);
    }
  };

  React.useEffect(() => () => clearTimeout(closeTimer.current), []);
  React.useEffect(() => {
    if (!isOpen) return;
    const dismiss = (event: PointerEvent | FocusEvent) => {
      if (event.target instanceof Node && (triggerRef.current?.contains(event.target) || popoverRef.current?.contains(event.target))) return;
      setIsOpen(false);
      setIsClickMode(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      triggerRef.current?.focus();
      setIsOpen(false);
      setIsClickMode(false);
    };
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('focusin', dismiss);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('focusin', dismiss);
      document.removeEventListener('keydown', escape);
    };
  }, [isOpen]);

  return (
    <div 
      ref={triggerRef}
      className="relative inline-block"
      role="button"
      tabIndex={0}
      aria-label={`View ${name} details`}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      aria-controls={isOpen ? popoverId : undefined}
      onMouseEnter={open}
      onMouseLeave={closeAfterLeave}
      onFocus={event => { if (event.target === event.currentTarget) open(); }}
      onClick={handleClick}
      onKeyDown={event => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
          setIsClickMode(true);
          popoverRef.current?.focus();
        }
      }}
    >
      <span 
        className="cursor-pointer"
      >
        {children}
      </span>
      
      {isOpen && ReactDOM.createPortal(
        <div 
          ref={popoverRef}
          id={popoverId}
          role="dialog"
          aria-label={`${name} details`}
          tabIndex={-1}
          className="fixed z-[99999] bg-white rounded-lg shadow-xl border-2 border-gray-300 p-3 w-[300px] max-w-[calc(100vw-16px)] max-h-[60vh] overflow-y-auto"
          style={{ top: `${position.top}px`, left: `${position.left}px` }}
          onMouseEnter={cancelClose}
          onMouseLeave={closeAfterLeave}
          onClick={event => event.stopPropagation()}
        >
            <div className="flex items-start gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <User className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-sm text-gray-900">{name}</p>
              </div>
            </div>
            
            <div className="space-y-2 text-sm pt-2 border-t border-gray-200 mb-3">
              {user?.email && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="h-4 w-4 flex-shrink-0" />
                  <a href={`mailto:${user.email}`} className="hover:text-blue-600 hover:underline break-all">
                    {user.email}
                  </a>
                </div>
              )}
              {user?.phone && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="h-4 w-4 flex-shrink-0" />
                  <a href={`tel:${user.phone}`} className="hover:text-blue-600 hover:underline">
                    {user.phone}
                  </a>
                </div>
              )}
            </div>

            {rsvpContext && (
              <section className="border-t pt-2 mt-2" aria-label="Event responses">
                <h3 className="text-sm font-semibold">RSVP by event</h3>
                {rsvpContext.length === 0 ? <p className="text-xs text-gray-500 mt-2">No assigned events</p> : (
                  <dl className="space-y-2 mt-2 text-sm">
                    {rsvpContext.map(response => (
                      <div key={response.eventId} className="flex justify-between gap-3">
                        <dt>{response.eventTitle}</dt>
                        <dd className="font-medium">{guestRsvpLabel(response.status)}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </section>
            )}

            {/* Quick Message Button */}
            {user && user.id > 0 && <div className="border-t pt-2 mt-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openQuickMessage({
                    recipientId: user.id,
                    recipientName: user.full_name,
                    celebrationId,
                  });
                  setIsOpen(false);
                  setIsClickMode(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              >
                <MessageCircle className="h-4 w-4" />
                Message {user.full_name}
              </button>
            </div>}
            <button type="button" className="mt-2 text-xs text-gray-600 hover:underline" onClick={() => {
              triggerRef.current?.focus();
              setIsOpen(false);
              setIsClickMode(false);
            }}>Close details</button>
          </div>,
        document.body
      )}
    </div>
  );
};
